/**
 * Every upload in feasibility lands in Files (docs/pdlc/feasibility-files.md §7).
 *
 * A button anywhere (the form, Visit history, Ask DANI, a DANI chat) reads its
 * file as it always did, then saves it here as the kind it was used as, so
 * nothing is uploaded twice and every file can be found again. The sheets are
 * the classifier's (`classifyFile`), so a workbook's Test and Control sheets
 * keep their groups wherever it is dropped.
 */
import type { ListKind, SavedList, SaveListInput } from '@/lib/feasibility/contracts/pql';
import { classifyFile, SPREADSHEET } from '@/lib/feasibility/files/classify';
import type { Sheet } from '@/lib/feasibility/ooh/location-lists';
import { csvRows } from '@/lib/feasibility/ooh/placements';

import { feasibilityApi } from '../../api';

type Api = Pick<typeof feasibilityApi, 'saveList'>;

/** A file's sheets: a workbook's every sheet, a CSV or text file as one; null for a brief file. */
export async function sheetsOfFile(f: File): Promise<Sheet[] | null> {
    if (SPREADSHEET.test(f.name)) {
        const { sheetsOf } = await import('@/lib/feasibility/ooh/read-workbook');
        return sheetsOf(f.name, await f.arrayBuffer());
    }
    if (/\.txt$/i.test(f.name)) return [{ name: f.name, rows: csvRows(await f.text()) }];
    return null;
}

const nameOf = (f: File) =>
    f.name
        .replace(/\.[a-z0-9]+$/i, '')
        .slice(0, 120)
        .padEnd(2, '_');

/**
 * Saves a file as a list of `kind`, from the sheets the classifier reads for
 * that kind (all of them when it reads none). Throws the save's own message.
 */
export async function saveFileAs(
    f: File,
    kind: ListKind,
    opts: { sheets?: Sheet[] | null; extra?: Partial<SaveListInput>; api?: Api } = {},
): Promise<SavedList> {
    const sheets = opts.sheets ?? (await sheetsOfFile(f)) ?? [];
    if (!sheets.length) throw new Error(`${f.name} has no rows to save.`);
    const reads = classifyFile(f.name, sheets);
    const read = reads.find((r) => r.kind === kind) ?? reads.find((r) => r.choices.includes(kind));
    const used = read?.sheets.length ? sheets.filter((s) => read.sheets.includes(s.name)) : sheets;
    const { list } = await (opts.api ?? feasibilityApi).saveList({
        kind,
        name: nameOf(f),
        sheets: used,
        fileName: f.name,
        ...(kind === 'campaign' ? { campaign: { name: nameOf(f).trim() } } : {}),
        ...opts.extra,
    });
    return list;
}

/**
 * A brief's attachments that are lists (station lists, ID lists, zip lists),
 * each saved as its own file. Screens are never saved this way: they need a
 * client and a network. A file that does not save is skipped; the brief has
 * already read it.
 */
export async function saveListsIn(files: File[], api?: Api): Promise<SavedList[]> {
    const saved: SavedList[] = [];
    for (const f of files) {
        const sheets = await sheetsOfFile(f).catch(() => null);
        if (!sheets) continue;
        for (const read of classifyFile(f.name, sheets)) {
            if (read.kind === 'brief' || read.kind === 'adlog' || read.kind === 'screens') continue;
            const used = sheets.filter((s) => read.sheets.includes(s.name));
            try {
                saved.push(await saveFileAs(f, read.kind, { sheets: used, api }));
            } catch {
                // Not a list the save can read: the brief reader still has it.
            }
        }
    }
    return saved;
}

/**
 * Keeps a file a section has already read in Files, without holding the
 * section up: the section works from its own read either way, and a save that
 * fails says so once.
 */
export function keepInFiles(f: File, kind: ListKind, sheets?: Sheet[] | null): void {
    void saveFileAs(f, kind, { sheets }).catch((err: Error) => {
        void import('react-toastify').then(({ toast }) =>
            toast.warn(`${f.name} was read, but not kept in Files: ${err.message}`),
        );
    });
}
